import PropTypes from 'prop-types';
import React from 'react';

import {
  DAYS,
  HOURS,
  MINUTES,
  SECONDS,
} from '../../resources/delivery-estimation-formatter/constants';
import { convertDurationToMaximumUnits } from '../../resources/delivery-estimation-formatter/deliveryEstimationFormatter';

// Used when comparing 2 rounded durations
const getUnitRank = (unit) => {
  switch (unit) {
    case DAYS:
      return 4;
    case HOURS:
      return 3;
    case MINUTES:
      return 2;
    case SECONDS:
      return 1;
    default:
      return 0;
  }
};

const getCostComparison = (twReceivedAmount, provReceivedAmount) => {
  if (twReceivedAmount > provReceivedAmount) {
    return 'BETTER';
  }
  if (twReceivedAmount < provReceivedAmount) {
    return 'WORSE';
  }
  return 'SAME';
};

const getSavings = (twReceivedAmount, provReceivedAmount) => twReceivedAmount - provReceivedAmount;

// TODO: optional?.chaining
// prettier-ignore
const canCompareDeliveryEstimations = (twEstimation, provEstimation) => (
  provEstimation
    && twEstimation
    && twEstimation.duration
    && provEstimation.duration
    && provEstimation.providerGivesEstimate
);

const getSpeedComparison = (twEstimation, provEstimation) => {
  // 0. Able to compare estimations
  if (!canCompareDeliveryEstimations(twEstimation, provEstimation)) {
    return null;
  }

  const {
    duration: { max: twMax, min: twMin },
  } = twEstimation;
  const {
    duration: { max: provMax, min: provMin },
  } = provEstimation;

  const twMaxDuration = twEstimation.duration.max;
  const provMaxDuration = provEstimation.duration.max;
  const twMinDuration = twEstimation.duration.min;
  const provMinDuration = provEstimation.duration.min;

  // 1. Compare MAX duration
  if (twMax && provMax) {
    // Round up in format { total: Number, units: String }
    const twMaxRounded = convertDurationToMaximumUnits(twMaxDuration);
    const provMaxRounded = convertDurationToMaximumUnits(provMaxDuration);

    // Are the durations an order of magnitude different?
    const twUnitRank = getUnitRank(twMaxRounded.units);
    const provUnitRank = getUnitRank(provMaxRounded.units);
    if (twUnitRank < provUnitRank) {
      return 'BETTER';
    }
    if (provUnitRank < twUnitRank) {
      return 'WORSE';
    }

    // No, units are same rank. Compare numeric value within range.
    if (twMaxRounded.total < provMaxRounded.total) {
      return 'BETTER';
    }
    if (provMaxRounded.total < twMaxRounded.total) {
      return 'WORSE';
    }

    // Max estimates are the same.
    // If no min value to compare, this is as far as we go.
    if (!provMin || !twMin) {
      return 'SAME';
    }
  }

  // 2. Compare MIN duration
  if (twMin && provMin) {
    // Round up in format { total: Number, units: String }
    const twMinRounded = convertDurationToMaximumUnits(twMinDuration);
    const provMinRounded = convertDurationToMaximumUnits(provMinDuration);

    // If both under a minute, considered the same
    if (twMinRounded.units === SECONDS && provMinRounded.units === SECONDS) {
      return 'SAME';
    }

    // Are the durations an order of magnitude different?
    const twUnitRank = getUnitRank(twMinRounded.units);
    const provUnitRank = getUnitRank(provMinRounded.units);
    if (twUnitRank < provUnitRank) {
      return 'BETTER';
    }
    if (provUnitRank < twUnitRank) {
      return 'WORSE';
    }

    // No, units are same rank. Compare numeric value within range.
    if (twMinRounded.total < provMinRounded.total) {
      return 'BETTER';
    }
    if (provMinRounded.total < twMinRounded.total) {
      return 'WORSE';
    }

    // Min estimates are the same.
    return 'SAME';
  }

  // Error state, not enough data provided
  return null;
};

/**
 * This Higher-Order Component accepts a quote from 2 providers
 * for the same route and compares them.
 * It passes in the correct props for <SendWithTw /> to render correctly.
 * @param {*} Component
 */
const withQuoteComparison = (Component) => {
  const Wrapped = ({
    baseProviderQuote,
    comparedProviderQuote,
    showDeliveryEstimates,
    sendAmount,
    onCallToActionClick,
  }) => {
    if (!comparedProviderQuote.supportedRoute) {
      return <Component supportedRoute={false} />;
    }

    const costComparison = getCostComparison(
      comparedProviderQuote.receivedAmount,
      baseProviderQuote.receivedAmount,
    );
    const savings = getSavings(
      comparedProviderQuote.receivedAmount,
      baseProviderQuote.receivedAmount,
    );
    const speedComparison = getSpeedComparison(
      comparedProviderQuote.deliveryEstimation,
      baseProviderQuote.deliveryEstimation,
    );

    const { sourceCurrency, targetCurrency, receivedAmount } = baseProviderQuote;

    return (
      <Component
        supportedRoute
        speedComparison={speedComparison}
        savings={savings}
        costComparison={costComparison}
        deliveryEstimate={comparedProviderQuote.deliveryEstimation}
        sourceCurrency={sourceCurrency}
        targetCurrency={targetCurrency}
        receivedAmount={receivedAmount}
        showDeliveryEstimates={showDeliveryEstimates}
        sendAmount={sendAmount}
        onCallToActionClick={onCallToActionClick}
      />
    );
  };

  Wrapped.propTypes = {
    baseProviderQuote: PropTypes.shape({
      receivedAmount: PropTypes.number,
      deliveryEstimation: PropTypes.shape({}),
      sourceCurrency: PropTypes.string,
      targetCurrency: PropTypes.string,
    }).isRequired,
    comparedProviderQuote: PropTypes.shape({
      supportedRoute: PropTypes.bool,
      receivedAmount: PropTypes.number,
      deliveryEstimation: PropTypes.shape({}),
    }).isRequired,
    showDeliveryEstimates: PropTypes.bool,
    sendAmount: PropTypes.number,
    onCallToActionClick: PropTypes.func,
  };

  Wrapped.defaultProps = {
    showDeliveryEstimates: false,
    sendAmount: 0,
    onCallToActionClick: () => {},
  };

  return Wrapped;
};

withQuoteComparison.propTypes = {
  Component: PropTypes.element.isRequired,
};

export default withQuoteComparison;
